// PastTrips.jsx
import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import "./PastTripsScreen.css";
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { formatTime, formatShortDate } from '../../utils/dateTimeUtils';
import { API_URL } from '../../index';
import {
  IonContent,
  IonPage,
  IonButton,
  IonIcon,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonCard,
  IonCardContent,
  IonList,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonText,
  IonSpinner,
  IonItem,
  IonAvatar,
  IonNote,
  IonInput
} from '@ionic/react';
import {
  arrowBackOutline,
  closeOutline,
  cashOutline,
  closeCircleOutline,
  shield,
  arrowBack
} from 'ionicons/icons';

// Configure axios with timeout
const apiClient = axios.create({
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const PastTrips = () => {
  const [selectedTab, setSelectedTab] = useState('driver');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [popupTripDetails, setPopupTripDetails] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, pastTripResultsRider: initialRiderTrips, pastTripResultsDriver: initialDriverTrips } = location.state;
  
  // State for trip data (allows refreshing from API)
  const [riderTrips, setRiderTrips] = useState(initialRiderTrips);
  const [driverTrips, setDriverTrips] = useState(initialDriverTrips);
  
  console.log("Past Trip Results Rider:", riderTrips);
  console.log("Past Trip Results Driver:", driverTrips);

  /**
   * fetchPastTrips
   * Fetches past trips from API for a given year and month
   */
  const fetchPastTrips = async (year, month) => {
    setIsLoading(true);
    try {
      const requestData = {
        UserID: user.user_id,
        Year: year,
        Month: month
      };

      const [riderResponse, driverResponse] = await Promise.allSettled([
        apiClient.post(`${API_URL}/pasttripsgeneraltorider`, requestData),
        apiClient.post(`${API_URL}/pasttripsgeneraltodriver`, requestData)
      ]);

      // Update rider trips if successful
      if (riderResponse.status === 'fulfilled' && riderResponse.value?.data) {
        setRiderTrips(riderResponse.value.data);
      }

      // Update driver trips if successful
      if (driverResponse.status === 'fulfilled' && driverResponse.value?.data) {
        setDriverTrips(driverResponse.value.data);
      }

      console.log(`Fetched trips for ${year}-${month}`);
    } catch (error) {
      console.error('Error fetching past trips:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Refetch trips when start date changes (extracts year/month from selected date)
  useEffect(() => {
    if (startDate) {
      const selectedDate = new Date(startDate);
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1; // getMonth() returns 0-11
      fetchPastTrips(year, month);
    }
  }, [startDate]);


  // Function to handle the "More Details" button click
  // Function to format numeric values safely
  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined) return 'N/A';
    const num = Number(value);
    if (Number.isNaN(num)) return 'N/A';
    return num.toFixed(decimals);
  };

  // Using centralized date/time formatting utilities
  const formatLocalDate = formatShortDate;
  const formatLocalTime = formatTime;

  // Function to handle the "More Details" button click
  const handleMoreDetailsClick = (trip) => {
    console.log("More Details clicked for trip:", trip);
    setPopupTripDetails(null);
    setShowDetailsModal(true);

    // Use the existing trip data directly for both driver and passenger trips
    console.log("Using existing trip data:", trip);
    setPopupTripDetails(trip);
  };

  // Function to close the modal
  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setPopupTripDetails(null);
  };

  // Derive filtered lists with basic validity checks
  const validDriverTrips = useMemo(() => {
    const driverTripsArray = driverTrips?.past_trips || driverTrips || [];
    return (Array.isArray(driverTripsArray) ? driverTripsArray : [])
      .filter((t) => t && t.TripDeparture_DateTime);
  }, [driverTrips]);

  const validPassengerTrips = useMemo(() => {
    const riderTripsArray = riderTrips?.past_trips || riderTrips || [];
    return (Array.isArray(riderTripsArray) ? riderTripsArray : [])
      .filter((t) => t && (t.arrivalAtEndLocDate || t.RiderStartLocName));
  }, [riderTrips]);

  // Calculate total earnings from completed driver trips
  const calculateTotalEarnings = () => {
    const completedDriverTrips = validDriverTrips.filter(trip => !trip.isCancelled);
    return completedDriverTrips.reduce((total, trip) => {
      const earnings = trip.TotalAmountEarned ? Number(trip.TotalAmountEarned) : 0;
      return total + (Number.isNaN(earnings) ? 0 : earnings);
    }, 0);
  };

  const totalEarnings = calculateTotalEarnings();

  return (
    <IonPage className="past-trips-page">
      <IonContent fullscreen className="past-trips-content">
        <div className="content-container">
          {/* Header with Back Button */}
          <div className="user-header">
            <IonButton
              fill="clear"
              className="back-button"
              onClick={() => navigate('/home')}
            >
              <IonIcon icon={arrowBackOutline} />
            </IonButton>
          </div>

          {/* Page Title */}
          <div className="page-title-section">
            <h2 className="page-title">Past Trips</h2>
          </div>

          {/* Tab Segment */}
          <IonSegment
            value={selectedTab}
            onIonChange={e => setSelectedTab(e.detail.value)}
            className="trips-segment"
          >
            <IonSegmentButton value="driver">
              <IonLabel>As Driver</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="passenger">
              <IonLabel>As Passenger</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {/* Date Selector */}
          <IonCard className="date-filter-card">
            <IonCardContent>
              <IonItem lines="none">
                <IonLabel position="stacked" className="date-filter-label">Select month</IonLabel>
                <IonInput
                  type="date"
                  value={startDate}
                  onIonInput={e => setStartDate(e.detail.value)}
                  className="date-filter-input"
                />
              </IonItem>
              {startDate && (
                <div className="clear-filter-container">
                  <IonButton
                    fill="clear"
                    size="small"
                    onClick={() => setStartDate('')}
                    className="clear-filter-button"
                  >
                    Clear filter
                  </IonButton>
                </div>
              )}
              {isLoading && (
                <div className="loading-indicator">
                  <IonSpinner name="crescent" />
                  <span>Loading trips...</span>
                </div>
              )}
            </IonCardContent>
          </IonCard>

          {/* Trip Content */}
          <div className="tab-content-ionic">
            {selectedTab === 'driver' ? (
              <>
                {/* Total Earnings Section for Driver */}
                {validDriverTrips.length > 0 && (
                  <IonCard className="earnings-summary-card">
                    <IonCardContent>
                      <div className="earnings-summary">
                        <div className="earnings-header">
                          <IonIcon icon={cashOutline} className="earnings-icon" />
                          <h3 className="earnings-title">Total Earnings</h3>
                        </div>
                        <div className="earnings-amount">
                          <span className="earnings-value">${formatNumber(totalEarnings)}</span>
                          <span className="earnings-label">from {validDriverTrips.filter(trip => !trip.isCancelled).length} completed trips</span>
                        </div>
                      </div>
                    </IonCardContent>
                  </IonCard>
                )}
                
                <IonList className="trip-list-ionic">
                {validDriverTrips.length > 0 ? (
                  validDriverTrips.map((trip, index) => (
                    <div 
                      key={index} 
                      className={`trip-card ${!trip.isCancelled ? 'clickable-card' : ''}`}
                      onClick={!trip.isCancelled ? () => handleMoreDetailsClick(trip) : undefined}
                    >
                      <div className="card-header">
                        <div className="driver-info">
                          <span className="driver-name">{formatLocalDate(trip.TripDeparture_DateTime)}</span>
                          <span className={`driver-tier ${trip.isCancelled ? 'cancelled-tier' : 'completed-tier'}`}>
                            {trip.isCancelled ? 'CANCELLED' : 'COMPLETED'}
                          </span>
                        </div>
                        <span className="trip-price">
                          {trip.TotalAmountEarned ? `$${formatNumber(trip.TotalAmountEarned)}` : 'No riders'}
                        </span>
                      </div>

                      {/* Origin Stop */}
                      <div className="origin-stop">
                        <div className="origin-circle"></div>
                        <span className="origin-time">{formatTime(trip.TripDeparture_DateTime)}</span>
                        <span className="origin-location">{trip.driverOriginName}</span>
                      </div>
                      
                      <div className="vertical-line"></div>

                      {/* Destination Stop */}
                      <div className="destination-stop">
                        <div className="destination-circle"></div>
                        <span className="destination-time">{formatTime(trip.TripArrival_DateTime)}</span>
                        <span className="destination-location">{trip.driverDestinationName}</span>
                      </div>

                      <div className="separator"></div>

                      {!trip.isCancelled && (
                        <div className="card-footer">
                          <div className="details-btn">
                            Details
                            <IonIcon icon={arrowBack} className="arrow" />
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <IonCard className="no-trips-card">
                    <IonCardContent>
                      <IonText className="no-trips-message">
                        <p>No past trips found.</p>
                      </IonText>
                    </IonCardContent>
                  </IonCard>
                )}
                </IonList>
              </>
            ) : (
              <IonList className="trip-list-ionic">
                {validPassengerTrips.length > 0 ? (
                  validPassengerTrips.map((trip, index) => (
                    <div 
                      key={index} 
                      className={`trip-card ${!trip.RiderTripCancelled ? 'clickable-card' : ''}`}
                      onClick={!trip.RiderTripCancelled ? () => handleMoreDetailsClick(trip) : undefined}
                    >
                      <div className="card-header">
                        <div className="driver-info passenger-driver-info">
                          <div className="trip-date-status">
                            <span className="trip-date">{formatLocalDate(trip.arrivalAtEndLocDate)}</span>
                            <span className={`driver-tier ${trip.RiderTripCancelled ? 'cancelled-tier' : 'completed-tier'}`}>
                              {trip.RiderTripCancelled ? 'CANCELLED' : 'COMPLETED'}
                            </span>
                          </div>
                          <div className="driver-name-row">
                            <span className="driver-name">{trip.driverName || 'Driver'}</span>
                            <IonIcon icon={shield} className="shield-icon" />
                            <span className={`driver-tier ${trip.RiderTripCancelled ? 'cancelled-tier' : 'verified-tier'}`}>Verified</span>
                          </div>
                        </div>
                        <span className="trip-price">
                          {trip.TotalCost ? `$${formatNumber(trip.TotalCost)}` : '—'}
                        </span>
                      </div>

                      {/* Start Location */}
                      <div className="origin-stop">
                        <div className="origin-circle"></div>
                        <span className="origin-time">Start</span>
                        <span className="origin-location">{trip.RiderStartLocName || 'N/A'}</span>
                      </div>
                      
                      <div className="vertical-line"></div>
                      
                      {/* End Location */}
                      <div className="destination-stop">
                        <div className="destination-circle"></div>
                        <span className="destination-time">{formatTime(trip.arrivalAtEndLocDate)}</span>
                        <span className="destination-location">{trip.RiderEndLocName || 'N/A'}</span>
                      </div>

                      <div className="separator"></div>

                      {!trip.RiderTripCancelled && (
                        <div className="card-footer">
                          <div className="details-btn">
                            Details
                            <IonIcon icon={arrowBack} className="arrow" />
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <IonCard className="no-trips-card">
                    <IonCardContent>
                      <IonText className="no-trips-message">
                        <p>No past trips found.</p>
                      </IonText>
                    </IonCardContent>
                  </IonCard>
                )}
              </IonList>
            )}
          </div>
        </div>

        {/* Trip Details Modal */}
        <IonModal isOpen={showDetailsModal} onDidDismiss={closeDetailsModal} className="trip-details-modal">
          <IonHeader>
            <IonToolbar color="dark">
              <IonTitle>Trip Details</IonTitle>
              <IonButton
                fill="clear"
                slot="end"
                onClick={closeDetailsModal}
                className="modal-close-button"
              >
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonToolbar>
          </IonHeader>
          <IonContent scrollY={true}>
            <div className="trip-details-popup-content">
              {/* Trip Details Content - ViewResultAsRider Style */}
              {popupTripDetails && typeof popupTripDetails === 'object' ? (
                <div className="trip-details-popup-container">
                  {/* Page Title */}
                  <div className="popup-title-section">
                    <h2 className="popup-title">Trip Details</h2>
                  </div>

                  <div className="popup-date-display">
                    {formatShortDate(popupTripDetails.TripDeparture_DateTime || popupTripDetails.arrivalAtEndLocDate)}
                  </div>

                  {/* Driver Card - Only show for rider trips */}
                  {selectedTab === 'passenger' && (
                    <div className="driver-card">
                      <div className="driver-info-compact">
                        <IonAvatar className="driver-avatar-compact">
                          <img
                            src="/default-avatar.png"
                            alt="Driver"
                            crossOrigin="anonymous"
                          />
                        </IonAvatar>
                        <div className="driver-details-compact">
                          <span className="driver-name-compact">
                            {popupTripDetails.driverName || 'N/A'}
                          </span>
                          <IonIcon icon={shield} className="shield-icon-compact" />
                          <span className="driver-tier-compact">Verified</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Price/Earnings Card */}
                  <div className="price-card">
                    <div className="price-details">
                      <span className="price-label">
                        {selectedTab === 'driver' ? 'Total Earnings' : 'Price'}
                      </span>
                      <span className="price-value">
                        {selectedTab === 'driver' ? (
                          popupTripDetails.TotalAmountEarned ? `$${formatNumber(popupTripDetails.TotalAmountEarned)}` : 'No riders'
                        ) : (
                          popupTripDetails.TotalCost ? `$${formatNumber(popupTripDetails.TotalCost)}` : 'N/A'
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Route Card */}
                  <div className="route-card">
                    <div className="route-details-compact">
                      {selectedTab === 'driver' ? (
                        <>
                          {/* Origin Stop */}
                          <div className="route-stop-compact">
                            <div className="origin-circle"></div>
                            <div className="stop-content-compact">
                              <span className="stop-label-compact">Origin</span>
                              <span className="stop-address-compact">
                                {popupTripDetails.driverOriginName || 'N/A'}
                              </span>
                              <div className="arrival-time-compact">
                                <IonNote>{formatTime(popupTripDetails.TripDeparture_DateTime)}</IonNote>
                              </div>
                            </div>
                          </div>
                          
                          <div className="vertical-line"></div>

                          {/* Destination Stop */}
                          <div className="route-stop-compact">
                            <div className="destination-circle"></div>
                            <div className="stop-content-compact">
                              <span className="stop-label-compact">Destination</span>
                              <span className="stop-address-compact">
                                {popupTripDetails.driverDestinationName || 'N/A'}
                              </span>
                              <div className="arrival-time-compact">
                                <IonNote>{formatTime(popupTripDetails.TripArrival_DateTime)}</IonNote>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Start */}
                          <div className="route-stop-compact">
                            <div className="origin-circle"></div>
                            <div className="stop-content-compact">
                              <span className="stop-label-compact">Start</span>
                              <span className="stop-address-compact">
                                {popupTripDetails.RiderStartLocName || 'N/A'}
                              </span>
                            </div>
                          </div>
                          
                          <div className="vertical-line"></div>

                          {/* Destination */}
                          <div className="route-stop-compact">
                            <div className="destination-circle"></div>
                            <div className="stop-content-compact">
                              <span className="stop-label-compact">Destination</span>
                              <span className="stop-address-compact">
                                {popupTripDetails.RiderEndLocName || 'N/A'}
                              </span>
                              <div className="arrival-time-compact">
                                <IonNote>{formatTime(popupTripDetails.arrivalAtEndLocDate)}</IonNote>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Trip Statistics Card */}
                  <div className="stats-card">
                    <div className="stats-grid">
                      <div className="stat-item">
                        <span className="stat-label">Total Distance</span>
                        <span className="stat-value">
                          {selectedTab === 'driver' ? (
                            popupTripDetails.TotalCarpoolDistance ? `${formatNumber(popupTripDetails.TotalCarpoolDistance)} km` : 'N/A'
                          ) : (
                            popupTripDetails.riderCarpoolDist ? `${formatNumber(popupTripDetails.riderCarpoolDist)} km` : 'N/A'
                          )}
                        </span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Status</span>
                        <span className="stat-value">
                          {popupTripDetails.isCancelled || popupTripDetails.RiderTripCancelled ? 'Cancelled' : 'Completed'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Handle error case */}
                  {popupTripDetails.Error && (
                    <div className="error-card">
                      <div className="error-content">
                        <IonIcon icon={closeCircleOutline} className="error-icon" color="danger" />
                        <IonText color="danger">
                          <p><strong>Error:</strong> {popupTripDetails.Error}</p>
                        </IonText>
                      </div>
                    </div>
                  )}
                </div>
              ) : popupTripDetails === null ? (
                <div className="loading-container">
                  <IonSpinner name="crescent" color="primary" />
                  <IonText>
                    <p>Loading trip details...</p>
                  </IonText>
                </div>
              ) : (
                <div className="error-card">
                  <div className="error-content">
                    <IonIcon icon={closeCircleOutline} className="error-icon" color="danger" />
                    <IonText color="danger">
                      <p>Error loading trip details. Unexpected data format.</p>
                    </IonText>
                  </div>
                </div>
              )}
            </div>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default PastTrips;


